<template>
  <div class="toaster-alerts">
    <transition-group name="toast" tag="div">
      <v-alert
        v-for="msg in messages"
        :key="msg.id"
        :type="msg.type"
        density="compact"
        variant="flat"
        class="toast-alert"
      >
        {{ t(msg.message, msg.values) }}
        <template #prepend>
          <Button
            icon-btn
            :color="msg.type === 'success' ? 'white' : msg.type === 'error' ? 'white' : 'black'"
            @click="dismiss(msg)"
            variant="plain"
          >
            <v-icon size="28">mdi-close-circle</v-icon>
          </Button>
        </template>
        <div
          v-if="getDuration(msg) > 0"
          class="progress-bar"
          :style="{ animationDuration: getDuration(msg) + 'ms' }"
        ></div>
      </v-alert>
    </transition-group>
  </div>
</template>

<script setup lang="ts">
import { computed, watch, onUnmounted } from 'vue'
import { useNotificationStore } from '@/stores/notification'
import type { ToasterMessage } from '@/entities/toaster-message'
import { useI18n } from 'vue-i18n'
import Button from '@/components/input-components/Button.vue'

const notificationStore = useNotificationStore()
const { t } = useI18n()

const messages = computed(() => notificationStore.messages)

const timers = new Map<number, ReturnType<typeof setTimeout>>()

function getDefaultDuration(type: ToasterMessage['type']): number {
  return type === 'error' ? 30000 : 20000
}

function getDuration(msg: ToasterMessage): number {
  return msg.duration ?? getDefaultDuration(msg.type)
}

function scheduleRemoval(msg: ToasterMessage) {
  if (!msg.id) return
  const duration = getDuration(msg)
  const timer = setTimeout(() => {
    notificationStore.deleteMessageById(msg.id!)
    timers.delete(msg.id!)
  }, duration)
  timers.set(msg.id, timer)
}

function dismiss(msg: ToasterMessage) {
  if (!msg.id) return
  const timer = timers.get(msg.id)
  if (timer) clearTimeout(timer)
  timers.delete(msg.id)
  notificationStore.deleteMessageById(msg.id!)
}

for (const msg of messages.value) {
  if (msg.id && !timers.has(msg.id)) {
    scheduleRemoval(msg)
  }
}

watch(
  () => messages.value.length,
  (newLen, oldLen) => {
    if (newLen > oldLen) {
      const newMsg = messages.value[newLen - 1]
      if (newMsg.id && !timers.has(newMsg.id)) {
        scheduleRemoval(newMsg)
      }
    }
  }
)

onUnmounted(() => {
  timers.forEach(timer => clearTimeout(timer))
  timers.clear()
})
</script>

<style lang="scss" scoped>
.toaster-alerts {
  position: fixed;
  top: 30px;
  left: 0;
  right: 0;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  pointer-events: none;

  > div {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
  }
}

.toast-alert {
  pointer-events: auto;
  width: fit-content;
  max-width: 90vw;
  min-width: 300px;
  margin-bottom: 10px;
  position: relative;
  overflow: hidden;
  white-space: normal;
  word-break: break-word;
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}
.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.progress-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background-color: rgba(var(--v-theme-black), 0.7);
  animation: shrink linear forwards;
}

@keyframes shrink {
  from { width: 100%; }
  to { width: 0; }
}

button {
  margin-left: 15px;
  margin-right: -10px;
  margin-top: -3px;
  height: 25px;
  width: 25px;
}

:deep(.v-alert:not(:has(.v-alert-title)) .v-alert__content) {
  padding: 0;
  line-height: 1.25;
}
</style>
